import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type UIEvent,
} from "react";
import { ListBox, ListBoxItem } from "react-aria-components";

import { cn } from "@/shared/lib/cn";
import {
  getMerryChatMinimapAnchorLayout,
  getMerryChatMinimapContentHeight,
  getMerryChatMinimapMarkerGeometry,
  getMerryChatMinimapNearestVisibleIndex,
  getMerryChatMinimapPreviewCenterY,
  MERRY_CHAT_MINIMAP_COMPONENT_ID,
  MERRY_CHAT_MINIMAP_PREVIEW_NODE_ID,
  MERRY_CHAT_MINIMAP_VIEWPORT_HEIGHT,
} from "@/shared/ui/merryChatMinimapVariants";

gsap.registerPlugin(useGSAP);

export interface MerryChatMinimapPreview {
  meta: string;
  summary: string;
}

export interface MerryChatMinimapItem {
  accessibleLabel: string;
  id: string;
  preview: MerryChatMinimapPreview;
}

export interface MerryChatMinimapProps {
  accessibleLabel: string;
  className?: string;
  items: readonly MerryChatMinimapItem[];
  onNavigate: (id: string) => void;
}

interface MerryChatMinimapCollectionItem extends MerryChatMinimapItem {
  anchorIndex: number;
  anchorLayout: ReturnType<typeof getMerryChatMinimapAnchorLayout>;
}

const markerMotion = {
  enterDuration: 0.18,
  enterEase: "power3.out",
  exitDuration: 0.14,
  exitEase: "power2.out",
} as const;

const previewMotion = {
  enterDuration: 0.24,
  enterEase: "power3.out",
  exitDuration: 0.16,
  exitEase: "power2.in",
  moveDuration: 0.18,
  moveEase: "power3.out",
} as const;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function hasFinePointer(pointerType: string) {
  return pointerType === "mouse" || pointerType === "pen";
}

export function MerryChatMinimap({
  accessibleLabel,
  className,
  items,
  onNavigate,
}: MerryChatMinimapProps) {
  const rootRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const pointerClientYRef = useRef<number | null>(null);
  const pointerIndexRef = useRef<number | null>(null);
  const didInitializeMotionRef = useRef(false);
  const [pointerIndex, setPointerIndex] = useState<number | null>(null);
  const [keyboardItemId, setKeyboardItemId] = useState<string | null>(null);
  const [displayedItem, setDisplayedItem] =
    useState<MerryChatMinimapItem | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const itemIndexes = useMemo(
    () => new Map(items.map((item, index) => [item.id, index])),
    [items],
  );
  const collectionItems = useMemo<readonly MerryChatMinimapCollectionItem[]>(
    () =>
      items.map((item, index) => ({
        ...item,
        anchorIndex: index,
        anchorLayout: getMerryChatMinimapAnchorLayout(index, items.length),
      })),
    [items],
  );
  const safePointerIndex =
    pointerIndex !== null && pointerIndex < items.length ? pointerIndex : null;
  const keyboardIndex =
    keyboardItemId === null ? null : (itemIndexes.get(keyboardItemId) ?? null);
  const activeIndex = safePointerIndex ?? keyboardIndex;
  const activeItem = activeIndex === null ? null : items[activeIndex] ?? null;
  const maxScrollTop = Math.max(
    getMerryChatMinimapContentHeight(items.length) -
      MERRY_CHAT_MINIMAP_VIEWPORT_HEIGHT,
    0,
  );
  const effectiveScrollTop = Math.min(scrollTop, maxScrollTop);
  const state = activeItem ? "on" : "off";
  const visualNodeId = activeItem ? "627:73908" : "627:73910";
  const previewCenterY =
    activeIndex === null
      ? MERRY_CHAT_MINIMAP_VIEWPORT_HEIGHT / 2
      : getMerryChatMinimapPreviewCenterY({
          index: activeIndex,
          itemCount: items.length,
          scrollTop: effectiveScrollTop,
        });

  const previewItem = activeItem ?? displayedItem;

  const updatePointerIndex = useCallback(() => {
    frameRef.current = null;
    const viewport = viewportRef.current;
    const pointerClientY = pointerClientYRef.current;

    if (!viewport || pointerClientY === null) {
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const nextIndex = getMerryChatMinimapNearestVisibleIndex({
      itemCount: items.length,
      pointerOffsetY: pointerClientY - viewportRect.top,
      scrollTop: viewport.scrollTop,
      viewportHeight: viewportRect.height,
    });

    if (pointerIndexRef.current !== nextIndex) {
      pointerIndexRef.current = nextIndex;
      const nextItem = nextIndex === null ? null : items[nextIndex];

      if (nextItem) {
        setDisplayedItem(nextItem);
      }

      setPointerIndex(nextIndex);
    }
  }, [items]);

  const schedulePointerUpdate = useCallback(() => {
    if (frameRef.current === null) {
      frameRef.current = window.requestAnimationFrame(updatePointerIndex);
    }
  }, [updatePointerIndex]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    },
    [],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (!hasFinePointer(event.pointerType)) {
        return;
      }

      pointerClientYRef.current = event.clientY;
      schedulePointerUpdate();
    },
    [schedulePointerUpdate],
  );

  const handlePointerDownCapture = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (hasFinePointer(event.pointerType)) {
        event.preventDefault();
      }
    },
    [],
  );

  const handlePointerLeave = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (!hasFinePointer(event.pointerType)) {
        return;
      }

      const currentPointerIndex = pointerIndexRef.current;

      if (currentPointerIndex !== null) {
        const currentItem = items[currentPointerIndex];

        if (currentItem) {
          setDisplayedItem(currentItem);
        }
      }

      pointerClientYRef.current = null;
      pointerIndexRef.current = null;
      setPointerIndex(null);
    },
    [items],
  );

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      setScrollTop(event.currentTarget.scrollTop);

      if (pointerClientYRef.current !== null) {
        schedulePointerUpdate();
      }
    },
    [schedulePointerUpdate],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLDivElement>, itemId: string) => {
      if (!event.currentTarget.matches(":focus-visible")) {
        return;
      }

      const index = itemIndexes.get(itemId);

      if (index === undefined) {
        return;
      }

      const focusedItem = items[index];

      if (focusedItem) {
        setDisplayedItem(focusedItem);
      }

      setKeyboardItemId(itemId);

      event.currentTarget.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "nearest",
      });
    },
    [itemIndexes, items],
  );

  const handleItemKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, itemId: string) => {
      if (
        event.key !== "ArrowUp" &&
        event.key !== "ArrowDown" &&
        event.key !== "Enter" &&
        event.key !== " "
      ) {
        return;
      }

      const index = itemIndexes.get(itemId);
      const item = index === undefined ? undefined : items[index];

      if (item) {
        setDisplayedItem(item);
        setKeyboardItemId(itemId);
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();

        if (index !== undefined) {
          const direction = event.key === "ArrowDown" ? 1 : -1;
          const nextIndex = Math.min(
            Math.max(index + direction, 0),
            items.length - 1,
          );
          const nextAnchor = rootRef.current?.querySelector<HTMLElement>(
            `[data-anchor-index="${nextIndex}"]`,
          );

          nextAnchor?.focus();
        }
      }

      if (event.key === " ") {
        event.preventDefault();

        if (!event.repeat) {
          onNavigate(itemId);
        }
      }
    },
    [itemIndexes, items, onNavigate],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<Element>) => {
      const nextTarget = event.relatedTarget;

      if (nextTarget instanceof Node && rootRef.current?.contains(nextTarget)) {
        return;
      }

      if (activeItem) {
        setDisplayedItem(activeItem);
      }

      setKeyboardItemId(null);
    },
    [activeItem],
  );

  useGSAP(
    () => {
      const root = rootRef.current;

      if (!root) {
        return;
      }

      const markerTargets = Array.from(
        root.querySelectorAll<HTMLElement>("[data-chat-minimap-marker-line]"),
      );
      const activeLayerTargets = Array.from(
        root.querySelectorAll<HTMLElement>("[data-chat-minimap-active-layer]"),
      );
      const previewPositioner = root.querySelector<HTMLElement>(
        "[data-chat-minimap-preview-positioner]",
      );
      const preview = root.querySelector<HTMLElement>(
        "[data-chat-minimap-preview]",
      );

      if (!previewPositioner || !preview) {
        return;
      }

      const targets = [
        ...markerTargets,
        ...activeLayerTargets,
        previewPositioner,
        preview,
      ];
      const isFirstRun = !didInitializeMotionRef.current;
      didInitializeMotionRef.current = true;
      const reducedMotion = prefersReducedMotion();

      gsap.killTweensOf(targets);

      const setMarkerState = (marker: HTMLElement, index: number) => {
        const geometry = getMerryChatMinimapMarkerGeometry(index, activeIndex);

        gsap.set(marker, {
          height: geometry.height,
          width: geometry.width,
        });
      };

      const setFinalState = () => {
        markerTargets.forEach(setMarkerState);
        activeLayerTargets.forEach((layer, index) => {
          gsap.set(layer, { autoAlpha: index === activeIndex ? 1 : 0 });
        });
        gsap.set(previewPositioner, { top: previewCenterY });
        gsap.set(preview, {
          autoAlpha: activeItem ? 1 : 0,
          scale: activeItem ? 1 : 0.97,
        });
        gsap.set(targets, { clearProps: "willChange" });
        root.dataset.chatMinimapAnimating = "false";
      };

      if (isFirstRun || reducedMotion) {
        setFinalState();
        return;
      }

      root.dataset.chatMinimapAnimating = "true";

      if (markerTargets.length > 0) {
        gsap.set(markerTargets, { willChange: "width,height" });
      }

      if (activeLayerTargets.length > 0) {
        gsap.set(activeLayerTargets, { willChange: "opacity" });
      }

      gsap.set(previewPositioner, { willChange: "top" });
      gsap.set(preview, { willChange: "opacity,transform" });

      const markerDuration = activeItem
        ? markerMotion.enterDuration
        : markerMotion.exitDuration;
      const markerEase = activeItem
        ? markerMotion.enterEase
        : markerMotion.exitEase;
      const timeline = gsap.timeline({
        defaults: {
          overwrite: "auto",
        },
        onComplete: () => {
          gsap.set(targets, { clearProps: "willChange" });
          root.dataset.chatMinimapAnimating = "false";
        },
      });

      markerTargets.forEach((marker, index) => {
        const geometry = getMerryChatMinimapMarkerGeometry(index, activeIndex);

        timeline.to(
          marker,
          {
            duration: markerDuration,
            ease: markerEase,
            height: geometry.height,
            width: geometry.width,
          },
          0,
        );
      });
      activeLayerTargets.forEach((layer, index) => {
        timeline.to(
          layer,
          {
            autoAlpha: index === activeIndex ? 1 : 0,
            duration: markerDuration,
            ease: markerEase,
          },
          0,
        );
      });
      timeline.to(
        previewPositioner,
        {
          duration: previewMotion.moveDuration,
          ease: previewMotion.moveEase,
          top: previewCenterY,
        },
        0,
      );
      timeline.to(
        preview,
        {
          autoAlpha: activeItem ? 1 : 0,
          duration: activeItem
            ? previewMotion.enterDuration
            : previewMotion.exitDuration,
          ease: activeItem
            ? previewMotion.enterEase
            : previewMotion.exitEase,
          scale: activeItem ? 1 : 0.97,
        },
        0,
      );
    },
    {
      dependencies: [activeIndex, activeItem, previewCenterY, items.length],
      scope: rootRef,
    },
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label={accessibleLabel}
      className={cn("merry-chat-minimap", className)}
      data-active-anchor-id={activeItem?.id}
      data-chat-minimap-animating="false"
      data-chat-minimap-state={state}
      data-figma-component-id={MERRY_CHAT_MINIMAP_COMPONENT_ID}
      data-figma-node-id={visualNodeId}
      onPointerDownCapture={handlePointerDownCapture}
      onPointerLeave={handlePointerLeave}
      onPointerMove={handlePointerMove}
      ref={rootRef}
    >
      <ListBox
        aria-label={accessibleLabel}
        className="merry-chat-minimap__viewport"
        items={collectionItems}
        onBlur={handleBlur}
        onScroll={handleScroll}
        orientation="vertical"
        ref={viewportRef}
        selectionMode="none"
      >
        {(item) => {
          const index = item.anchorIndex;
          const geometry = getMerryChatMinimapMarkerGeometry(
            index,
            activeIndex,
          );

          return (
            <ListBoxItem
              aria-label={item.accessibleLabel}
              className={({ isFocusVisible }) =>
                cn(
                  "merry-chat-minimap__anchor",
                  isFocusVisible &&
                    "merry-chat-minimap__anchor--focus-visible",
                )
              }
              data-anchor-id={item.id}
              data-anchor-index={index}
              data-testid={`chat-minimap-anchor-${item.id}`}
              id={item.id}
              onFocus={(event) => handleFocus(event, item.id)}
              onAction={() => onNavigate(item.id)}
              onKeyDown={(event) => handleItemKeyDown(event, item.id)}
              style={{ height: item.anchorLayout.height }}
              textValue={item.accessibleLabel}
            >
              <span
                aria-hidden="true"
                className="merry-chat-minimap__marker"
                data-chat-minimap-marker-line=""
                style={{
                  height: geometry.height,
                  top: item.anchorLayout.markerCenterY,
                  width: geometry.width,
                }}
              >
                <span
                  className="merry-chat-minimap__active-layer"
                  data-chat-minimap-active-layer=""
                />
              </span>
            </ListBoxItem>
          );
        }}
      </ListBox>

      <span
        aria-hidden={!activeItem || undefined}
        className="merry-chat-minimap__preview-positioner"
        data-chat-minimap-preview-positioner=""
        style={{ top: previewCenterY } as CSSProperties}
      >
        <span
          className="merry-chat-minimap__preview"
          data-chat-minimap-preview=""
          data-figma-node-id={MERRY_CHAT_MINIMAP_PREVIEW_NODE_ID}
        >
          <span aria-hidden="true" className="merry-chat-minimap__preview-glass" />
          <span className="merry-chat-minimap__preview-content">
            <span className="merry-chat-minimap__preview-meta">
              {previewItem?.preview.meta ?? ""}
            </span>
            <span className="merry-chat-minimap__preview-summary">
              {previewItem?.preview.summary ?? ""}
            </span>
          </span>
          <span aria-hidden="true" className="merry-chat-minimap__preview-stroke" />
        </span>
      </span>
    </nav>
  );
}
